import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { InspectionStatus } from "@/hera-api/client/types.gen";
import {
  type FlowStepKey,
  INSPECTION_FLOW_EDGES,
  INSPECTION_FLOW_LAYOUT,
  INSPECTION_FLOW_NODE_ORDER,
} from "@/lib/inspection-status-info";
import { getInspectionStatusDisplay } from "@/lib/utils";
import { Info } from "lucide-react";
import { useMemo, useState } from "react";

const FLOW_CELL_WIDTH = 200;
const FLOW_CELL_HEIGHT = 56;

function getFlowStepDisplay(step: FlowStepKey) {
  if (step === "AWAITING_CHECK") {
    return getInspectionStatusDisplay("REQUIRES_PAYMENT", true);
  }
  if (step === "PAYMENT_FAILED") {
    return { label: "Payment Failed", variant: "destructive" as const };
  }
  return getInspectionStatusDisplay(step as InspectionStatus);
}

function nodeCenter(row: number, col: number) {
  return {
    x: col * FLOW_CELL_WIDTH + FLOW_CELL_WIDTH / 2,
    y: row * FLOW_CELL_HEIGHT + FLOW_CELL_HEIGHT / 2,
  };
}

const HALF_CELL_W = FLOW_CELL_WIDTH / 2;
const HALF_CELL_H = FLOW_CELL_HEIGHT / 2;

/** Estimated half-width and half-height of a badge from its label (text-xs). Used so lines sit close to small labels and don't overlap long ones. */
function estimatedLabelHalfSize(label: string): {
  halfW: number;
  halfH: number;
} {
  const halfW = Math.max(20, (label.length * 6 + 24) / 2);
  const halfH = 12;
  return { halfW, halfH };
}

/** Point on the boundary of a node. halfSize = estimated label size so lines match the badge; when useLeftRightOnly, force left/right. */
function cellEdgePoint(
  center: { x: number; y: number },
  towards: { x: number; y: number },
  useLeftRightOnly?: boolean,
  halfSize?: { halfW: number; halfH: number },
): { x: number; y: number } {
  const hw = halfSize?.halfW ?? HALF_CELL_W;
  const hh = halfSize?.halfH ?? HALF_CELL_H;
  if (useLeftRightOnly) {
    return towards.x >= center.x
      ? { x: center.x + hw, y: center.y }
      : { x: center.x - hw, y: center.y };
  }
  const dx = towards.x - center.x;
  const dy = towards.y - center.y;
  if (dx === 0 && dy === 0) return { ...center };
  const candidates: { t: number; side: "left" | "right" | "top" | "bottom" }[] =
    [];
  if (dx !== 0) {
    candidates.push({
      t: dx > 0 ? hw / dx : -hw / dx,
      side: dx > 0 ? "right" : "left",
    });
  }
  if (dy !== 0) {
    candidates.push({
      t: dy > 0 ? hh / dy : -hh / dy,
      side: dy > 0 ? "bottom" : "top",
    });
  }
  const best = candidates
    .filter((c) => c.t > 0)
    .reduce((a, b) => (a.t < b.t ? a : b));
  const side = best.side;
  if (side === "left") return { x: center.x - hw, y: center.y };
  if (side === "right") return { x: center.x + hw, y: center.y };
  if (side === "top") return { x: center.x, y: center.y - hh };
  return { x: center.x, y: center.y + hh };
}

function InspectionFlowDiagram() {
  const { width, height, edgesWithCoords } = useMemo(() => {
    const rows = 7;
    const cols = 5;
    const width = cols * FLOW_CELL_WIDTH;
    const height = rows * FLOW_CELL_HEIGHT;
    const edgesWithCoords = INSPECTION_FLOW_EDGES.map((edge) => {
      const fromPos = INSPECTION_FLOW_LAYOUT[edge.from];
      const toPos = INSPECTION_FLOW_LAYOUT[edge.to];
      if (!fromPos || !toPos) return null;
      const fromCenter = nodeCenter(fromPos.row, fromPos.col);
      const toCenter = nodeCenter(toPos.row, toPos.col);
      const fromLabel = getFlowStepDisplay(edge.from).label;
      const toLabel = getFlowStepDisplay(edge.to).label;
      return {
        ...edge,
        fromCenter,
        toCenter,
        fromPos,
        toPos,
        fromHalfSize: estimatedLabelHalfSize(fromLabel),
        toHalfSize: estimatedLabelHalfSize(toLabel),
      };
    }).filter(
      (
        e,
      ): e is (typeof INSPECTION_FLOW_EDGES)[number] & {
        fromCenter: { x: number; y: number };
        toCenter: { x: number; y: number };
        fromPos: { row: number; col: number };
        toPos: { row: number; col: number };
        fromHalfSize: { halfW: number; halfH: number };
        toHalfSize: { halfW: number; halfH: number };
      } => e != null,
    );
    return { width, height, edgesWithCoords };
  }, []);

  return (
    <div
      className="relative overflow-auto rounded-md border border-border bg-muted/30 p-4"
      style={{ minWidth: width, minHeight: height }}
    >
      {/* Grid: one node per status */}
      <div
        className="relative grid gap-0"
        style={{
          width,
          height,
          gridTemplateColumns: `repeat(5, ${FLOW_CELL_WIDTH}px)`,
          gridTemplateRows: `repeat(7, ${FLOW_CELL_HEIGHT}px)`,
        }}
      >
        {INSPECTION_FLOW_NODE_ORDER.map((nodeId) => {
          const pos = INSPECTION_FLOW_LAYOUT[nodeId];
          if (!pos) return null;
          const { label, variant } = getFlowStepDisplay(nodeId);
          return (
            <div
              key={nodeId}
              className="flex items-center justify-center"
              style={{
                gridRow: pos.row + 1,
                gridColumn: pos.col + 1,
              }}
            >
              <Badge variant={variant} className="pointer-events-none text-xs">
                {label}
              </Badge>
            </div>
          );
        })}
      </div>

      {/* Edges: arrows with trigger labels */}
      <svg
        className="pointer-events-none absolute left-4 top-4 overflow-visible"
        width={width}
        height={height}
        aria-hidden
      >
        <title>Inspection status flow diagram</title>
        <defs>
          <marker
            id="flow-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="4"
            orient="auto"
          >
            <path
              d="M0,0 L8,4 L0,8 Z"
              fill="currentColor"
              className="text-muted-foreground"
            />
          </marker>
        </defs>
        {edgesWithCoords.map((edge, i) => {
          const {
            fromCenter,
            toCenter,
            trigger,
            fromPos,
            toPos,
            fromHalfSize,
            toHalfSize,
          } = edge;
          const useLeftRight = fromPos.col !== toPos.col;
          const fromEdge = cellEdgePoint(
            fromCenter,
            toCenter,
            useLeftRight,
            fromHalfSize,
          );
          const toEdge = cellEdgePoint(
            toCenter,
            fromCenter,
            useLeftRight,
            toHalfSize,
          );
          const isPaymentProcessingToAwaitingReg =
            edge.from === "PAYMENT_PROCESSING" &&
            edge.to === "PAID_PENDING_REVIEW";

          let pathD: string;
          let labelX: number;
          let labelY: number;

          if (isPaymentProcessingToAwaitingReg) {
            const cornerX = toCenter.x;
            const cornerY = fromEdge.y;
            const toBottomCenterY = toCenter.y + toHalfSize.halfH;
            pathD = `M ${fromEdge.x} ${fromEdge.y} L ${cornerX} ${cornerY} L ${cornerX} ${toBottomCenterY}`;
            labelX = (fromEdge.x + cornerX) / 2;
            labelY = cornerY;
          } else {
            pathD = `M ${fromEdge.x} ${fromEdge.y} L ${toEdge.x} ${toEdge.y}`;
            labelX = (fromCenter.x + toCenter.x) / 2;
            labelY = (fromCenter.y + toCenter.y) / 2;
          }

          return (
            <g key={`${edge.from}-${edge.trigger}-${edge.to}-${i}`}>
              <path
                d={pathD}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                markerEnd="url(#flow-arrow)"
                className="text-muted-foreground"
              />
              {trigger
                ? (() => {
                    const labelW = Math.max(24, trigger.length * 6 + 12);
                    const labelH = 16;
                    return (
                      <>
                        <rect
                          x={labelX - labelW / 2}
                          y={labelY - labelH / 2}
                          width={labelW}
                          height={labelH}
                          rx={2}
                          className="fill-background stroke-border"
                          strokeWidth={0.5}
                        />
                        <text
                          x={labelX}
                          y={labelY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-muted-foreground text-xs font-medium"
                        >
                          {trigger}
                        </text>
                      </>
                    );
                  })()
                : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

interface InspectionStatusInfoPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InspectionStatusInfoPanel({
  open,
  onOpenChange,
}: InspectionStatusInfoPanelProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="min-w-[1040px] max-w-[1200px] max-h-[90vh] flex flex-col"
        showCloseButton
        elevated
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Inspection status flow</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="pb-4 space-y-3">
            <InspectionFlowDiagram />
            <p className="text-muted-foreground text-xs border-t border-border pt-3">
              Exempt inspections display as &quot;Exempt&quot; only after the
              registration is approved; before that they display as
              &quot;Awaiting Registration Approval&quot;.
            </p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

/** Button that opens the inspection status info panel. Place near inspection status display. */
export function InspectionStatusInfoButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={() => setOpen(true)}
        aria-label="View inspection status information"
      >
        <Info className="h-3.5 w-3.5" />
      </Button>
      <InspectionStatusInfoPanel open={open} onOpenChange={setOpen} />
    </>
  );
}
